import React, { useRef, useEffect, useState } from "react";
import { checkTimestampStatus, getByPath, validity } from '../features/Sensors/SensorUtils';
import IMeasurement from '../interfaces/measurement.interface';
import { useAppSelector } from '../hooks';

interface Parameter {
    alias: string;
    column?: string;
    decimals?: number;
    graph?: boolean;
    id: number;
    mode?: string;
    p064?: string;
    transform?: string;
}

interface SensorProps {
    sensorName: string;
    deviceName: string;
    sensorData: Parameter[];
    frequency: number;
    collapsed: boolean;
}

export default function Sensor({ deviceName, sensorName, sensorData, frequency, collapsed }: SensorProps) {
    const dataRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState("auto");
    const [selfCollapsed, setSelfCollapsed] = useState(false);
    const measurements = useAppSelector(state => state.Measurements.measurements);

    const isCollapsed = collapsed || selfCollapsed;

    useEffect(() => {
        if (dataRef.current) {
            setHeight(isCollapsed ? "0px" : `${dataRef.current.scrollHeight}px`);
        }
    }, [isCollapsed, sensorData]);

    const handleToggleSelf = () => {
        setSelfCollapsed(prev => !prev);
    };

    return (
        <div className="sensor">
            <div className="sensor-head flex justify-between">
                <div className="head flex justify-between">
                    <h4>{deviceName}</h4>
                </div>
                <p onClick={handleToggleSelf} className="cursor-pointer select-none">...</p>
            </div>

            <div
                className="sensor-data"
                ref={dataRef}
                style={{
                    maxHeight: height,
                    transition: "max-height 0.5s ease-in-out",
                    overflow: "hidden",
                }}
            >
                {sensorData.map((param, index) => {
                    const path = `${deviceName}.${sensorName}.${param.column}`;
                    const data = getByPath(path, measurements);
                    const status: validity = checkTimestampStatus(data?.timestamp, frequency);

                    return (
                        <div key={index} className="flex justify-between gap-1 border-b py-2">
                            <p className="font-semibold">{param.alias}</p>
                            <p className={`flex justify-between gap-1 ${status.color}`}>
                                {data?.value}
                                {status.icon}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
